import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { Add, Close, DeleteOutline, ImageRounded, Remove } from '@mui/icons-material';
import { getDownloadURL, ref } from 'firebase/storage';
import { storage } from '../../firebase/firebase';
import { TIME_INTERVAL } from '../../constants';
import { formatMinutes } from '../../time/timeUtils';
import {
  Employee, MAX_SERVICE_QUESTIONS, Service, SERVICE_QUESTION_LABEL_LIMIT, ServiceQuestion,
} from '../../types';
import { serviceImagePath, ServiceDraft, useServiceActions } from './useServiceActions';
import { editDialogContentSx, editDialogPaperSx } from '../global/editDialogLayout';

export const MAX_SERVICE_TITLE_LENGTH = 30;
export const MAX_SERVICE_DESCRIPTION_LENGTH = 200;
const DEFAULT_DURATION = 30;

type ServiceFormDialogProps = {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  /** People or resources the service can be assigned to. */
  staff: Employee[];
  categories: string[];
  /** e.g. "Team members", "Chairs". */
  staffLabel: string;
  /** Edit this service; omit to add a new one. */
  service?: Service;
  onSaved?: (service: Service, action: 'added' | 'updated' | 'deleted') => void;
};

type FieldErrors = Partial<Record<'title' | 'price' | 'time' | 'employees' | 'questions', string>>;

const newQuestionId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() :
    `q-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const emptyDraft = (staff: Employee[]): ServiceDraft => ({
  title: '',
  description: '',
  price: 0,
  time: DEFAULT_DURATION,
  // A business with one person or resource never has to pick it.
  employees: staff.length === 1 ? [staff[0].id] : [],
  category: '',
  showOnHomepage: true,
  priceVaries: false,
  questions: [],
});

const staffName = (person: Employee) => [person.firstName, person.lastName].filter(Boolean).join(' ').trim();

const useImagePreview = (file: File | undefined, storagePath: string | undefined) => {
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    if (file) {
      const objectUrl = URL.createObjectURL(file);
      setUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }
    if (!storagePath) {
      setUrl(undefined);
      return undefined;
    }
    let active = true;
    getDownloadURL(ref(storage, storagePath))
      .then((downloadUrl) => { if (active) setUrl(downloadUrl); })
      .catch(() => { if (active) setUrl(undefined); });
    return () => { active = false; };
  }, [file, storagePath]);
  return url;
};

const sectionLabel = (text: string) => (
  <Typography variant="overline" color="text.secondary" fontWeight={800} letterSpacing=".08em">{text}</Typography>
);

/**
 * The one add/edit service form. Used by the Services page and the Site Builder.
 */
const ServiceFormDialog: React.FC<ServiceFormDialogProps> = ({
  open, onClose, tenantId, staff, categories, staffLabel, service, onSaved,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { add, update, remove } = useServiceActions(tenantId);
  const [draft, setDraft] = useState<ServiceDraft>(() => service ? { ...service } : emptyDraft(staff));
  const [priceText, setPriceText] = useState('');
  const [image, setImage] = useState<File>();
  const [imageRemoved, setImageRemoved] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [submitError, setSubmitError] = useState<string>();

  useEffect(() => {
    if (!open) return;
    const initial = service ? { ...service } : emptyDraft(staff);
    setDraft(initial);
    setPriceText(service ? String(service.price) : '');
    setImage(undefined);
    setImageRemoved(false);
    setErrors({});
    setConfirmDelete(false);
    setSubmitError(undefined);
    // Reset only when the dialog opens or switches service.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, service?.id]);

  const existingImagePath = imageRemoved ? undefined : service?.imagePath;
  const previewUrl = useImagePreview(image, existingImagePath);
  const hasImage = Boolean(image || existingImagePath);
  const busy = add.isPending || update.isPending || remove.isPending;
  const editing = Boolean(service);
  const sortedStaff = useMemo(
    () => [...staff].sort((left, right) => staffName(left).localeCompare(staffName(right))),
    [staff]
  );

  const set = <K extends keyof ServiceDraft>(key: K, value: ServiceDraft[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
    if (key in errors) setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const updateQuestion = (index: number, change: Partial<ServiceQuestion>) => {
    set('questions', (draft.questions ?? []).map((question, item) => item === index ? { ...question, ...change } : question));
  };

  const toggleStaff = (id: string) => {
    set('employees', draft.employees.includes(id)
      ? draft.employees.filter((existing) => existing !== id)
      : [...draft.employees, id]);
  };

  const validate = (): FieldErrors => {
    const next: FieldErrors = {};
    const title = draft.title.trim();
    const price = Number(priceText || 0);
    if (!title) next.title = 'Give the service a name.';
    else if (title.length > MAX_SERVICE_TITLE_LENGTH) next.title = `Keep it to ${MAX_SERVICE_TITLE_LENGTH} characters.`;
    if (!draft.priceVaries && (!Number.isFinite(price) || price < 0)) next.price = 'Enter a price of 0 or more.';
    if ((draft.questions ?? []).some((question) => !question.label.trim())) {
      next.questions = 'Fill in each question, or remove the empty one.';
    }
    if (!draft.time || draft.time <= 0) next.time = 'Set how long it takes.';
    if (!draft.employees.length) next.employees = `Choose at least one of your ${staffLabel.toLowerCase()}.`;
    return next;
  };

  const submit = async () => {
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    setSubmitError(undefined);
    const values: ServiceDraft = {
      ...draft,
      title: draft.title.trim(),
      description: (draft.description ?? '').trim(),
      // "Price varies" services are booked at $0; staff set the final price later.
      price: draft.priceVaries ? 0 : Math.round(Number(priceText || 0) * 100) / 100,
      priceVaries: draft.priceVaries === true,
      questions: (draft.questions ?? []).map((question) => ({ ...question, label: question.label.trim() })),
      category: draft.category || undefined,
      showOnHomepage: hasImage && draft.showOnHomepage !== false,
    };
    try {
      if (service) {
        const next: Service = { ...service, ...values };
        if (image) next.imagePath = serviceImagePath(tenantId, values.title);
        else if (imageRemoved) delete next.imagePath;
        if (!next.category) delete next.category;
        onSaved?.(await update.mutateAsync({ original: service, service: next, image }), 'updated');
      } else {
        if (!values.category) delete values.category;
        onSaved?.(await add.mutateAsync({ service: values, image }), 'added');
      }
      onClose();
    } catch (error) {
      console.error('Saving the service failed', error);
      setSubmitError('The service could not be saved. Check your connection and try again.');
    }
  };

  const deleteService = async () => {
    if (!service) return;
    try {
      onSaved?.(await remove.mutateAsync(service), 'deleted');
      onClose();
    } catch (error) {
      console.error('Deleting the service failed', error);
      setSubmitError('The service could not be deleted. Try again.');
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}
      PaperProps={{ sx: editDialogPaperSx(fullScreen) }}>
      <Box sx={{ position: 'relative', px: { xs: 2.5, sm: 3.5 }, pt: { xs: 2.5, sm: 3 }, pb: 2,
        '&::before': { content: '""', position: 'absolute', inset: '0 0 auto', height: 5,
          background: 'linear-gradient(90deg, #1976d2 0%, #44a3ff 52%, #74d4ff 100%)' } }}>
        <Stack direction="row" alignItems="flex-start" spacing={2}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" component="h2" fontWeight={900}>{editing ? 'Edit service' : 'Add a service'}</Typography>
            <Typography color="text.secondary" sx={{ mt: .5 }}>
              {editing ? 'Changes apply to new bookings right away.' : 'Customers will be able to book it right away.'}
            </Typography>
          </Box>
          <IconButton aria-label="Close" onClick={onClose} disabled={busy}><Close /></IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ ...editDialogContentSx, px: { xs: 2.5, sm: 3.5 }, pt: 1, pb: 3 }}>
        <Stack spacing={3}>
          <Box>
            {sectionLabel('Photo')}
            <Box sx={{ mt: 1, position: 'relative', borderRadius: 3.5, overflow: 'hidden', aspectRatio: '16 / 9',
              border: hasImage ? '1px solid' : '2px dashed', borderColor: hasImage ? 'divider' : 'rgba(25,118,210,.35)',
              bgcolor: hasImage ? 'grey.100' : 'rgba(25,118,210,.04)' }}>
              {hasImage && previewUrl ? (
                <Box component="img" src={previewUrl} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              ) : (
                <Stack spacing={1} alignItems="center" justifyContent="center" sx={{ height: '100%', color: 'primary.main' }}>
                  <ImageRounded sx={{ fontSize: 36 }} />
                  <Typography fontWeight={800}>Add a photo</Typography>
                  <Typography variant="caption" color="text.secondary">Services with a photo can appear on your homepage</Typography>
                </Stack>
              )}
              <Stack direction="row" spacing={1} sx={{ position: 'absolute', right: 12, bottom: 12 }}>
                <Button component="label" variant="contained" size="small" disabled={busy}
                  sx={{ borderRadius: 999, fontWeight: 800, boxShadow: 3 }}>
                  {hasImage ? 'Replace' : 'Choose photo'}
                  <input hidden type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file?.type.startsWith('image/')) {
                      setImage(file);
                      setImageRemoved(false);
                    }
                    event.target.value = '';
                  }} />
                </Button>
                {hasImage ? (
                  <Button variant="contained" color="inherit" size="small" disabled={busy}
                    onClick={() => { setImage(undefined); setImageRemoved(true); }}
                    sx={{ borderRadius: 999, fontWeight: 800, bgcolor: 'background.paper', boxShadow: 3 }}>
                    Remove
                  </Button>
                ) : null}
              </Stack>
            </Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}
              sx={{ mt: 1.5, p: 1.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', opacity: hasImage ? 1 : .6 }}>
              <Box>
                <Typography fontWeight={800}>Show on homepage</Typography>
                <Typography variant="body2" color="text.secondary">
                  {hasImage ? 'Feature this service in your website’s Services section.' : 'Add a photo to feature it on your website.'}
                </Typography>
              </Box>
              <Switch checked={hasImage && draft.showOnHomepage !== false} disabled={!hasImage || busy}
                onChange={(event) => set('showOnHomepage', event.target.checked)}
                inputProps={{ 'aria-label': 'Show on homepage' }} />
            </Stack>
          </Box>

          <Stack spacing={2}>
            {sectionLabel('Details')}
            <TextField label="Service name" value={draft.title} disabled={busy}
              onChange={(event) => set('title', event.target.value)}
              inputProps={{ maxLength: MAX_SERVICE_TITLE_LENGTH }}
              error={Boolean(errors.title)}
              helperText={errors.title ?? `${draft.title.length}/${MAX_SERVICE_TITLE_LENGTH}`} />
            <TextField label="Description (optional)" value={draft.description ?? ''} disabled={busy} multiline minRows={3}
              onChange={(event) => set('description', event.target.value)}
              inputProps={{ maxLength: MAX_SERVICE_DESCRIPTION_LENGTH }}
              helperText={`${(draft.description ?? '').length}/${MAX_SERVICE_DESCRIPTION_LENGTH}`} />
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}
              sx={{ p: 1.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <Box>
                <Typography fontWeight={800}>Price varies</Typography>
                <Typography variant="body2" color="text.secondary">
                  {draft.priceVaries ?
                    'Customers see “Price varies” and pay at the visit. You set the final price on the booking.' :
                    'Turn on if the price depends on the job.'}
                </Typography>
              </Box>
              <Switch checked={draft.priceVaries === true} disabled={busy}
                onChange={(event) => set('priceVaries', event.target.checked)}
                inputProps={{ 'aria-label': 'Price varies' }} />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              {draft.priceVaries ? null : <TextField label="Price" value={priceText} disabled={busy} inputMode="decimal" placeholder="0.00"
                sx={{ flex: 1 }}
                onChange={(event) => {
                  const value = event.target.value.replace(/[^\d.]/g, '');
                  if (/^\d*(\.\d{0,2})?$/.test(value)) {
                    setPriceText(value);
                    if (errors.price) setErrors((current) => ({ ...current, price: undefined }));
                  }
                }}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                error={Boolean(errors.price)} helperText={errors.price ?? 'Use 0 for free services'} />}
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}
                  sx={{ height: 56, px: 1, borderRadius: 1, border: '1px solid',
                    borderColor: errors.time ? 'error.main' : 'rgba(0,0,0,.23)' }}>
                  <IconButton aria-label="Shorter" disabled={busy || draft.time <= TIME_INTERVAL}
                    onClick={() => set('time', Math.max(TIME_INTERVAL, draft.time - TIME_INTERVAL))}><Remove /></IconButton>
                  <Box sx={{ flex: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary" display="block" lineHeight={1}>Duration</Typography>
                    <Typography fontWeight={800}>{draft.time ? formatMinutes(draft.time) : '—'}</Typography>
                  </Box>
                  <IconButton aria-label="Longer" disabled={busy || draft.time >= 480}
                    onClick={() => set('time', Math.min(480, draft.time + TIME_INTERVAL))}><Add /></IconButton>
                </Stack>
                <Typography variant="caption" color={errors.time ? 'error' : 'text.secondary'} sx={{ ml: 1.75 }}>
                  {errors.time ?? `${TIME_INTERVAL}-minute steps`}
                </Typography>
              </Box>
            </Stack>
            {categories.length ? (
              <TextField select label="Category (optional)" value={draft.category ?? ''} disabled={busy}
                onChange={(event) => set('category', event.target.value)}>
                <MenuItem value=""><em>No category</em></MenuItem>
                {categories.map((category) => <MenuItem key={category} value={category}>{category}</MenuItem>)}
              </TextField>
            ) : null}
          </Stack>

          <Box>
            {sectionLabel('Questions for the customer')}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Ask for anything you need to know before the visit. Customers answer when they book.
            </Typography>
            <Stack spacing={1.25}>
              {(draft.questions ?? []).map((question, index) => (
                <Stack key={question.id} direction="row" spacing={1} alignItems="center">
                  <TextField size="small" fullWidth label={`Question ${index + 1}`} value={question.label} disabled={busy}
                    placeholder="Type a question"
                    inputProps={{ maxLength: SERVICE_QUESTION_LABEL_LIMIT }}
                    onChange={(event) => updateQuestion(index, { label: event.target.value })} />
                  <Stack direction="row" alignItems="center" sx={{ flexShrink: 0 }}>
                    <Checkbox size="small" checked={question.required} disabled={busy}
                      onChange={(event) => updateQuestion(index, { required: event.target.checked })}
                      inputProps={{ 'aria-label': `Question ${index + 1} required` }} />
                    <Typography variant="body2">Required</Typography>
                  </Stack>
                  <IconButton aria-label={`Remove question ${index + 1}`} disabled={busy}
                    onClick={() => set('questions', (draft.questions ?? []).filter((_, item) => item !== index))}>
                    <DeleteOutline />
                  </IconButton>
                </Stack>
              ))}
              {(draft.questions ?? []).length < MAX_SERVICE_QUESTIONS ? (
                <Button startIcon={<Add />} disabled={busy} sx={{ alignSelf: 'flex-start', fontWeight: 800 }}
                  onClick={() => set('questions', [...(draft.questions ?? []), { id: newQuestionId(), label: '', required: false }])}>
                  Add a question
                </Button>
              ) : null}
              {errors.questions ? <Typography variant="caption" color="error">{errors.questions}</Typography> : null}
            </Stack>
          </Box>

          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              {sectionLabel(`Who performs it`)}
              {sortedStaff.length > 1 ? (
                <Button size="small" disabled={busy} onClick={() => set('employees',
                  draft.employees.length === sortedStaff.length ? [] : sortedStaff.map((person) => person.id))}>
                  {draft.employees.length === sortedStaff.length ? 'Clear' : 'Select all'}
                </Button>
              ) : null}
            </Stack>
            {sortedStaff.length ? (
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {sortedStaff.map((person) => {
                  const selected = draft.employees.includes(person.id);
                  return (
                    <Chip key={person.id} label={staffName(person) || 'Unnamed'} disabled={busy}
                      onClick={() => toggleStaff(person.id)}
                      avatar={<Avatar>{(person.firstName || '?').slice(0, 1).toUpperCase()}</Avatar>}
                      color={selected ? 'primary' : 'default'} variant={selected ? 'filled' : 'outlined'}
                      aria-pressed={selected}
                      sx={{ height: 40, borderRadius: 999, fontWeight: 700, px: .5 }} />
                  );
                })}
              </Box>
            ) : (
              <Alert severity="warning" sx={{ mt: 1 }}>Add {staffLabel.toLowerCase()} first, then assign them here.</Alert>
            )}
            {errors.employees ? <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>{errors.employees}</Typography> : null}
          </Box>

          {submitError ? <Alert severity="error">{submitError}</Alert> : null}
          {confirmDelete ? (
            <Alert severity="warning" action={<Stack direction="row" spacing={1}>
              <Button color="inherit" size="small" disabled={busy} onClick={() => setConfirmDelete(false)}>Keep</Button>
              <Button color="error" variant="contained" size="small" disabled={busy} onClick={() => void deleteService()}>
                {remove.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </Stack>}>
              Delete “{service?.title}”? This can’t be undone.
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>

      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: { xs: 2.5, sm: 3.5 }, py: 1.75,
        borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper',
        pb: { xs: 'max(14px, env(safe-area-inset-bottom))', sm: 1.75 } }}>
        {editing && !confirmDelete ? (
          <Button color="error" startIcon={<DeleteOutline />} disabled={busy} onClick={() => setConfirmDelete(true)}>
            Delete
          </Button>
        ) : null}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={busy} sx={{ fontWeight: 800 }}>Cancel</Button>
        <Button variant="contained" onClick={() => void submit()} disabled={busy}
          sx={{ minHeight: 46, px: 3, borderRadius: 3, fontWeight: 900 }}>
          {add.isPending || update.isPending ? 'Saving…' : editing ? 'Save changes' : 'Add service'}
        </Button>
      </Stack>
    </Dialog>
  );
};

export default ServiceFormDialog;
